import { useEffect, useState } from "react";
import { FiTrash, FiEdit2 } from "react-icons/fi";
import { HiCash, HiReceiptRefund } from "react-icons/hi";
import { API_URL } from '../../config/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import styles from "../../styles/FinanceView.module.css";
import CreateWalletModal from "./modal/CreateWalletModal";
import MoneyActionModal from "./MoneyActionModal";


const CardWalletsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState("today");
  const [showModal, setShowModal] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  const [moneyModalOpen, setMoneyModalOpen] = useState(false);
  const [moneyType, setMoneyType] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);

  const COLORS = ["#4F46E5","#22C55E","#F97316","#EF4444","#06B6D4","#A855F7"];
  const getColor = (str) => {
    if (!str) return COLORS[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return COLORS[Math.abs(hash) % COLORS.length];
  };

  // Вынесли fetchCardWallets наружу, чтобы вызывать вручную
  const fetchCardWallets = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/wallets?type=card`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const contentType = res.headers.get("content-type") || "";
      const text = await res.text(); // читаем как текст в любом случае

      if (!res.ok) {
        console.error("HTTP error:", res.status, text);
        throw new Error(`Ошибка загрузки: ${res.status}`);
      }

      if (!contentType.includes("application/json")) {
        console.error("Ожидали JSON, получили:", contentType, text.slice(0, 200));
        throw new Error("Сервер вернул не JSON (скорее всего HTML)");
      }

      const json = JSON.parse(text);
      setData(json.data || []);
    } catch (error) {
      console.error("Ошибка card wallets:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchCardWallets();
  }, []);

  const formatDate = (date) => new Date(date).toLocaleDateString("ru-RU");

  const handleEdit = (item) => {
    setEditingWallet(item);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    console.log("Удалить:", id);
  };

  const handleDeposit = (item) => {
    setSelectedWallet(item);
    setMoneyType("deposit");
    setMoneyModalOpen(true);
  };

  const handleWithdraw = (item) => {
    setSelectedWallet(item);
    setMoneyType("withdraw");
    setMoneyModalOpen(true);
  };

  // после успешного POST просто обновляем таблицу
  const handleMoneySuccess = () => {
    fetchCardWallets(); // перезагружаем данные с сервера
  };

  const chartData = data.map((item) => ({
    name: item.bank_name || item.iik || `Счет ${item.id}`,
    balance: parseFloat(item.balance || 0),
    fill: getColor(item.iik || item.id.toString()),
  }));

  const shortenLabel = (label, maxLen = 10) =>
    label.length > maxLen ? label.slice(0, maxLen) + "…" : label;

  return (
    <div className={styles.financeContainer}>
      <div className={styles.timeFilter}>
        {["today", "yesterday", "week", "month"].map((f) => (
          <button
            key={f}
            className={`${styles.timeBtn} ${timeFilter === f ? styles.activeTimeBtn : ""}`}
            onClick={() => setTimeFilter(f)}
          >
            {f === "today" ? "Сегодня" : f === "yesterday" ? "Вчера" : f === "week" ? "Неделя" : "Месяц"}
          </button>
        ))}
      </div>

      <div className={styles.tableWrapper}>
        <button className={styles.add} onClick={() => setShowModal(true)}>+</button>

        {showModal && (
          <CreateWalletModal
            wallet={editingWallet}
            onClose={() => {
              setShowModal(false);
              setEditingWallet(null);
            }}
            onCreated={(updatedItem) => {
              if (editingWallet) {
                setData((prev) => prev.map((w) => (w.id === updatedItem.id ? updatedItem : w)));
              } else {
                setData((prev) => [...prev, updatedItem]);
              }
            }}
          />
        )}

        {loading ? (
          <p className={styles.loading}>Загрузка...</p>
        ) : data.length === 0 ? (
          <p className={styles.empty}>Нет данных</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Номер счета</th>
                <th>Банк</th>
                <th>Дата создания</th>
                <th>Сумма</th>
                <th style={{ textAlign: "center" }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.id}>
                  <td>{item.iik || "—"}</td>
                  <td>{item.bank_name || "—"}</td>
                  <td>{item.inserted_at ? formatDate(item.inserted_at) : "—"}</td>
                  <td className={styles.balance}>{parseFloat(item.balance || 0).toLocaleString()} ₸</td>
                  <td className={styles.actionsCell}>
                    <button className={styles.depositBtn} title="Ввод денег" onClick={() => handleDeposit(item)}>
                      <HiCash size={20} />
                    </button>
                    <button className={styles.withdrawBtn} title="Вывод денег" onClick={() => handleWithdraw(item)}>
                      <HiReceiptRefund size={20} />
                    </button>
                    <button className={styles.editBtn} title="Редактировать" onClick={() => handleEdit(item)}>
                      <FiEdit2 size={18} />
                    </button>
                    <button className={styles.deleteBtn} title="Удалить" onClick={() => handleDelete(item.id)}>
                      <FiTrash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {moneyModalOpen && selectedWallet && (
        <MoneyActionModal
          wallet={selectedWallet}
          type={moneyType}
          onClose={() => {
            setMoneyModalOpen(false);
            setSelectedWallet(null);
            setMoneyType(null);
          }}
          onSuccess={handleMoneySuccess} // обновление таблицы после POST
        />
      )}

      {data.length > 0 && (
        <div className={styles.chartWrapper}>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tickFormatter={shortenLabel} interval={0} angle={-30} textAnchor="end" />
              <YAxis />
              <Tooltip formatter={(value) => value.toLocaleString("ru-RU", { minimumFractionDigits: 2 }) + " ₸"} />
              <Legend />
              <Bar dataKey="balance" name="Баланс" maxBarSize={40} radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} stroke={entry.fill} strokeWidth={1} />
                ))}
                <LabelList dataKey="balance" position="top" formatter={(val) => val.toLocaleString()} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default CardWalletsTable;
